import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaBuilding,
  FaMapMarkerAlt,
  FaBriefcase,
  FaTimes,
  FaBusinessTime,
} from "react-icons/fa";
import { LuDownload } from "react-icons/lu";
import { formatDate } from "../../utilities/formatDate";

const JobDetailsModal = ({ job, isOpen, onClose }) => {
  const navigate = useNavigate();
  if (!isOpen) return null;

  const handleApplyClick = () => {
    navigate("/apply-job");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-[70%] px-8 py-6 flex flex-col h-[85%]">
        {/* Modal Header */}
        <div className="flex justify-between items-center border-b pb-4 mb-4">
          <h2 className="pl-10 text-3xl font-bold text-green-700">
            {job.title || "CMI IT SPECIALIST (SHORT TERM STAFF)"} -{" "}
            <span className=" font-bold">
              {job.reference || " REF/HR/05/021"}
            </span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-red-500"
          >
            <FaTimes size={22} />
          </button>
        </div>

        {/* Institution, Location, and Level */}
        <div className="flex flex-wrap gap-10 mb-5 text-gray-500 font-semibold pl-10">
          <div className="flex items-center">
            <FaBuilding size={20} className="mr-[10px]" />
            <span>{job.institution || "EAC Secretariat"}</span>
          </div>
          <div className="flex items-center">
            <FaMapMarkerAlt size={20} className="mr-[10px]" />
            <span>{job.location || "Arusha, Tanzania"}</span>
          </div>
          <div className="flex items-center">
            <FaBriefcase size={20} className="mr-[10px]" />
            <span>{job.level || "Grade P2"}</span>
          </div>
          <div className="flex items-center">
            <FaBusinessTime size={25} className="mr-[10px]" />
            <span>{job.duration || "5 Years"}</span>
          </div>
        </div>

        {/* Modal Content - Scrollable */}
        <div className="flex-1 overflow-y-auto pr-4">
          {/* Dates */}
          <div className="flex flex-wrap gap-14 mb-6 text-gray-600 font-medium pl-10">
            <div className="text-gray-700 mb-2">
              <span className="font-medium">Date Posted:</span>{" "}
              <span className="font-bold text-gray-600">
                {formatDate(job.deadline)}
              </span>
            </div>
            <div className="text-gray-700 mb-2">
              <span className="font-medium">Application Deadline:</span>{" "}
              <span className="font-bold text-red-500">
                {formatDate(job.deadline)}
              </span>
            </div>
          </div>

          {/* Introductory Section */}
          <p className="text-gray-700 leading-relaxed pl-10 mb-8">
            {job.purpose || (
              <>
                The East African Community is a regional intergovernmental
                organization comprising the Republic of Burundi, Democratic
                Republic of Congo, the Republic of Kenya, Federal Republic of
                Somalia, the Republic of Rwanda , Republic of South Sudan, the
                Republic of Uganda and the United Republic of Tanzania with its
                Headquarters in Arusha, Tanzania. The EAC's mission is to widen
                and deepen economic, political, social and cultural integration
                to improve the quality of life of the people of East Africa
                through increased competitiveness, value added production, trade
                and investments. <br />
                The Community established Capital Markets Infrastructure in
                2020. The overall objective is to allow a seamless movement of
                securities and payments between the different EAC Capital
                Markets, compatible at the regional level, in order to enable
                the creation of a regional financial market in the EAC region.{" "}
                <br />
                The East African Community Secretariat seeks to recruit a highly
                motivated, result-driven, and qualified professional from
                citizens of the Republic of Burundi, Democratic Republic of
                Congo, Republic of Kenya, Republic of Rwanda, Republic of South
                Sudan, Republic of Uganda and the United Republic of Tanzania to
                fill the above position tenable at the EAC Secretariat in
                Arusha, Tanzania.
              </>
            )}
          </p>

          {/* Job details */}
          <div className="flex flex-wrap gap-7 mb-4 pl-16 text-gray-600 font-medium">
            <div className="flex items-center">
              <span>Directorate:</span>{" "}
              <span className="text-gray-900 font-semibold">
                {job.directorate || " CUSTOMS AND TRADE"}
              </span>
            </div>
            <div className="flex items-center">
              <span>Reports To:</span>{" "}
              <span className="text-gray-900 font-semibold">
                {job.reference || " DIRECTOR CUSTOMS AND TRADE"}
              </span>
            </div>
          </div>

          <div className="pl-16">
            {/* Job Purpose */}
            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Job Purpose
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {job.purpose ||
                  "The CMI IT Specialist will be responsible for the maintenance of the CMI database by planning, implementing, monitoring, and improving performance of the databases and related IT infrastructure. To carry out his/her tasks, the CMI IT Specialist will be required to maintain liaison with relevant stakeholders including IT staff in the central banks, Stock Exchanges and Central Securities Depositories across the EAC Partner States, EAC Secretariat and other relevant EAC organs. "}
              </p>
            </section>

            {/* Duties and Responsibilities */}
            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Duties and Responsibilities
              </h3>
              <ul className="list-decimal list-inside space-y-2 text-gray-700 pl-5">
                {job?.duties?.length > 0 ? (
                  job.duties.map((duty, index) => <li key={index}>{duty}</li>)
                ) : (
                  <>
                    <li>
                      Managing and providing support for the Capital Markets
                      Infrastructure (CMI) information systems;
                    </li>
                    <li>
                      Coordinating and overseeing smooth operations of the CMI
                      information systems;
                    </li>
                    <li>
                      Ensuring compliance to the CMI Operational rules and
                      procedures by all participants;
                    </li>
                    <li>
                      Training new users on basic CMI network functions, CMI
                      system access and software programs as needed;
                    </li>
                    <li>
                      Managing business operations continuity requests for the
                      CMI;
                    </li>
                    <li>
                      Installing and when necessary upgrading the database
                      servers and application tools of the CMI as and when
                      necessary;
                    </li>
                    <li>
                      Plan database upgrades by maintaining, evaluating, and
                      improving a transaction processing model of the CMI;{" "}
                    </li>
                    <li>
                      Assess database performance of the CMI by developing a
                      protocol for measurement of results and identification of
                      problem areas;
                    </li>
                    <li>
                      Regular improvement of the CMI databases, applications and
                      network performance by monitoring their performance;
                    </li>
                    <li>
                      Evaluating and resolving processing and programming
                      problems, and designing database management tools;
                    </li>
                  </>
                )}
              </ul>
            </section>

            {/* Minimum Requirements */}
            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Qualifications and Requirements
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 pl-5">
                {job?.requirements?.length > 0 ? (
                  job.requirements.map((req, index) => (
                    <li key={index}>{req}</li>
                  ))
                ) : (
                  <>
                    <li>
                      A Master’s degree or its equivalent in Computer Science,
                      IT and/or related fields. A professional qualification in
                      related fields will be an added advantage e.g. Certified
                      UNIX Administrator or Microsoft certified engineer. E
                    </li>

                    <li>
                      At least 8 years of relevant experience, at middle level
                      management or above, working in: central banks Central
                      Depository Systems environment, Real Time Gross Settlement
                      (RTGS) database environment, or Automatic Trading Systems.
                    </li>
                    <li>
                      Experience in implementing Capital Markets Systems will be
                      added advantage. Experience and demonstrated knowledge of
                      Oracle relational database systems, database logic
                      knowledge and report writing skills are required.
                    </li>
                  </>
                )}
              </ul>
            </section>

            {/* Job Purpose */}
            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Skills and Competencies
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 pl-5">
                {job?.requirements?.length > 0 ? (
                  job.requirements.map((req, index) => (
                    <li key={index}>{req}</li>
                  ))
                ) : (
                  <>
                    <li>
                      Strong technical and analytical capabilities for analyzing
                      customs and trade issues.
                    </li>

                    <li>
                      Fluent in English. Knowledge of Kiswahili and French is
                      desirable. Excellent oral, written and presentation
                      skills.
                    </li>
                    <li>
                      Excellent organizational skills, ability to handle
                      multiple tasks without compromising quality and team
                      spirit building skills.
                    </li>
                    <li>
                      Computer literate with competency in the use of Microsoft
                      Outlook and Office products such as Word, Excel, Power
                      Point.
                    </li>
                    <li>
                      Strong interpersonal and communication skills; negotiation
                      skills and ability to work harmoniously in a diverse
                      environment.
                    </li>
                  </>
                )}
              </ul>
            </section>

            {/* Terms and Conditions */}
            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Eligibility for application
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {job.purpose ||
                  "Applicants from only Republic of Burundi, Democratic Republic of Congo, Republic of Kenya, Republic of Rwanda, Republic of South Sudan, Republic of Uganda and the United Republic of Tanzania are eligible to apply for the above position."}
              </p>
            </section>

            {/* Job Purpose */}
            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Terms and Conditions of Service
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 pl-5">
                {job?.requirements?.length > 0 ? (
                  job.requirements.map((req, index) => (
                    <li key={index}>{req}</li>
                  ))
                ) : (
                  <>
                    <li>
                      The above position is tenable for a contract of five (5)
                      years renewable once.
                    </li>
                    <li>
                      This position is subject to the application of the Quota
                      System.
                    </li>
                  </>
                )}
              </ul>
            </section>
            {/* Job Purpose */}
            <section className="mb-8">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Additional Information
              </h3>
              <ul className="list-disc list-inside space-y-2 text-gray-700 pl-5">
                {job?.requirements?.length > 0 ? (
                  job.requirements.map((req, index) => (
                    <li key={index}>{req}</li>
                  ))
                ) : (
                  <>
                    <li>
                      All candidates applying must have qualifications that are
                      recognized by the relevant national accreditation body in
                      their respective Partner State. This condition is
                      applicable for locally and internationally attained
                      qualifications.
                    </li>
                    <li>
                      All professions that require registration with the
                      specific professional bodies will be expected to do so in
                      compliance with the requirements of their respective
                      Partner States.
                    </li>

                    <li>
                      Internship, training and apprenticeship will not be
                      considered as relevant work experience.
                    </li>
                    <li>
                      The EAC is an equal opportunity employer; therefore,
                      female candidates are particularly encouraged to apply.
                      EAC will only respond to those candidates who strictly
                      meet the set requirements.
                    </li>

                    <li>
                      EAC Staff Rules and Regulations preclude considerations of
                      applicants above 55 years of age.
                    </li>
                    <li>
                      You may submit your application either electronically or
                      in hard copy but not both.
                    </li>
                    <li>
                      Please note that EAC does not require candidates to pay
                      money for the recruitment process. All invitations for
                      interviews will be done in writing.
                    </li>
                    <li>
                      A due diligence will be conducted for all successful
                      candidates in respect of their academic certificates
                      (certification by a recognized higher education body in
                      respective Partner States) and employment records.
                    </li>
                  </>
                )}
              </ul>
            </section>
          </div>
        </div>

        {/* Footer - Fixed at Bottom */}
        <div className="flex justify-between px-14 items-center border-t pt-4">
          <button className="flex items-center text-green-700 hover:text-blue-500  font-semibold">
            <LuDownload size={24} className="mr-2" />
            Download Job Description
          </button>
          <button
            onClick={handleApplyClick}
            className="bg-green-700 text-white font-semibold hover:shadow-xl py-3 px-14 mr-10 rounded-xl hover:bg-green-600"
          >
            Apply for Job
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobDetailsModal;
